import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import { Box, IconButton, Slider } from "@mui/material";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ShakaPlayerAudio, {
  playShakaAudioExclusive,
} from "@/shared/components/shakaplayer/ShakaPlayerAudio";
import {
  dismissCardAudioShakaMiniPlayer,
  getShakaAudioLiveMedia,
  useCardAudioShakaMiniPlayerStore,
} from "../cardAudioShakaMiniPlayerStore";
import {
  getActiveCardAudio,
  pauseActiveCardAudio,
  subscribeCardAudioState,
} from "../singleCardAudio";
import { useCardVideoMiniPlayerStore } from "../cardVideoMiniPlayerStore";

/** Floating mini audio player — persists while browsing card pages (bottom-right). */
export function CardAudioMiniPlayer() {
  const videoMiniVisible = useCardVideoMiniPlayerStore((s) => s.visible);

  // ----- Shaka mini player (persistent engine lives here) -----
  const shakaVisible = useCardAudioShakaMiniPlayerStore((s) => s.visible);
  const shakaSrc = useCardAudioShakaMiniPlayerStore((s) => s.src);
  const shakaTitle = useCardAudioShakaMiniPlayerStore((s) => s.title);
  const shakaArtist = useCardAudioShakaMiniPlayerStore((s) => s.artist);
  const shakaArtwork = useCardAudioShakaMiniPlayerStore((s) => s.artwork);
  const shakaPlaying = useCardAudioShakaMiniPlayerStore((s) => s.playing);
  const shakaCurrentTime = useCardAudioShakaMiniPlayerStore((s) => s.currentTime);
  const shakaDuration = useCardAudioShakaMiniPlayerStore((s) => s.duration);
  const showShakaMiniPlayer = useCardAudioShakaMiniPlayerStore((s) => s.showMiniPlayer);
  const playRequestId = useCardAudioShakaMiniPlayerStore((s) => s.playRequestId);

  // ----- Normal file audio -----
  const [hasTrack, setHasTrack] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [artwork, setArtwork] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const syncFrom = (audio: HTMLAudioElement | null) => {
      if (!audio) {
        setHasTrack(false);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        return;
      }

      const hasSource = Boolean(audio.currentSrc || audio.src);
      const ended = audio.ended || false;
      const shouldShow = hasSource && (!ended || audio.currentTime > 0);
      setHasTrack(shouldShow);
      setIsPlaying(!audio.paused && !ended);
      setCurrentTime(audio.currentTime || 0);
      setDuration(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0);
      setTitle(audio.dataset.singleCardTitle || "Now playing");
      setArtist(audio.dataset.singleCardArtist || "");
      setArtwork(audio.dataset.singleCardArtwork || "");
    };

    const bindTrackedAudio = (audio: HTMLAudioElement) => {
      const onPlay = () => {
        setIsDismissed(false);
        syncFrom(audio);
      };
      const onSync = () => syncFrom(audio);
      const onEnded = () => {
        syncFrom(audio);
        if (audio.ended) setHasTrack(false);
      };

      audio.addEventListener("play", onPlay);
      audio.addEventListener("pause", onSync);
      audio.addEventListener("timeupdate", onSync);
      audio.addEventListener("loadedmetadata", onSync);
      audio.addEventListener("ended", onEnded);

      return () => {
        audio.removeEventListener("play", onPlay);
        audio.removeEventListener("pause", onSync);
        audio.removeEventListener("timeupdate", onSync);
        audio.removeEventListener("loadedmetadata", onSync);
        audio.removeEventListener("ended", onEnded);
      };
    };

    let unbindTracked: (() => void) | null = null;

    const unsubscribe = subscribeCardAudioState((audio) => {
      unbindTracked?.();
      unbindTracked = null;
      if (audio) {
        unbindTracked = bindTrackedAudio(audio);
        syncFrom(audio);
      } else {
        syncFrom(null);
      }
    });

    return () => {
      unsubscribe();
      unbindTracked?.();
    };
  }, []);

  if (typeof document === "undefined") return null;

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const bottomStack = videoMiniVisible
    ? { xs: "calc(max(12px, env(safe-area-inset-bottom, 12px)) + 212px)", sm: "232px" }
    : { xs: "max(12px, env(safe-area-inset-bottom, 12px))", sm: 20 };

  const shellSx = {
    position: "fixed" as const,
    right: { xs: 12, sm: 18 },
    left: "auto",
    bottom: bottomStack,
    zIndex: 1700,
    width: isExpanded ? { xs: "calc(100vw - 24px)", sm: 380 } : { xs: "72vw", sm: 360 },
    maxWidth: 420,
    borderRadius: isExpanded ? "18px" : "999px",
    px: isExpanded ? 1.4 : 1.25,
    py: isExpanded ? 1.2 : 0.9,
    color: "#fff",
    background:
      "linear-gradient(165deg, rgba(47,48,54,0.94) 0%, rgba(31,32,36,0.92) 48%, rgba(21,22,27,0.95) 100%)",
    border: "1px solid rgba(255,255,255,0.14)",
    backdropFilter: "blur(16px) saturate(160%)",
    boxShadow: "0 14px 36px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08)",
    transition: "all 0.22s ease",
  };

  // ---------- Shaka mini player ----------
  // Engine stays mounted while visible → page changes do NOT restart audio.
  if (shakaVisible && shakaSrc) {
    const getMedia = () => getShakaAudioLiveMedia();

    const toggleShakaPlayPause = () => {
      const media = getMedia();
      if (!media) return;
      if (media.paused) {
        void playShakaAudioExclusive(media).catch(() => {});
      } else {
        media.pause();
      }
    };

    const seekShaka = (_: Event, value: number | number[]) => {
      const next = Array.isArray(value) ? value[0] : value;
      if (!Number.isFinite(next)) return;
      const media = getMedia();
      if (!media) return;
      media.currentTime = next;
      useCardAudioShakaMiniPlayerStore.getState().setPlayback({ currentTime: next });
    };

    const jumpShaka = (delta: number) => {
      const media = getMedia();
      if (!media) return;
      if (!Number.isFinite(media.duration) || media.duration <= 0) return;
      const next = Math.min(Math.max(0, media.currentTime + delta), media.duration);
      media.currentTime = next;
      useCardAudioShakaMiniPlayerStore.getState().setPlayback({ currentTime: next });
    };

    const stopShaka = () => {
      dismissCardAudioShakaMiniPlayer();
      setIsExpanded(false);
    };

    return createPortal(
      <>
        {/* Persistent engine: key = src only → remount only when track changes */}
        <ShakaPlayerAudio key={shakaSrc} src={shakaSrc} playRequestId={playRequestId} />

        {showShakaMiniPlayer && (
          <Box role="region" aria-label="Mini audio player" sx={shellSx}>
            <MiniPlayerChrome
              title={shakaTitle}
              artist={shakaArtist}
              artwork={shakaArtwork}
              isPlaying={shakaPlaying}
              isExpanded={isExpanded}
              currentTime={shakaCurrentTime}
              duration={shakaDuration}
              onToggleExpand={() => setIsExpanded((v) => !v)}
              onTogglePlayPause={toggleShakaPlayPause}
              onSeek={seekShaka}
              onJump={jumpShaka}
              onStop={stopShaka}
              formatTime={formatTime}
            />
          </Box>
        )}
      </>,
      document.body
    );
  }

  // ---------- Normal file (<audio>) mini player ----------
  if (!hasTrack || isDismissed) return null;

  const audio = getActiveCardAudio();
  if (!audio) return null;

  const togglePlayPause = () => {
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  const seekTo = (_: Event, value: number | number[]) => {
    const next = Array.isArray(value) ? value[0] : value;
    if (!Number.isFinite(next)) return;
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const jump = (delta: number) => {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const next = Math.min(Math.max(0, audio.currentTime + delta), audio.duration);
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const stopPlayback = () => {
    pauseActiveCardAudio();
    setCurrentTime(0);
    setIsPlaying(false);
    setIsDismissed(true);
    setIsExpanded(false);
    setArtwork("");
  };

  return createPortal(
    <Box role="region" aria-label="Mini audio player" sx={shellSx}>
      <MiniPlayerChrome
        title={title}
        artist={artist}
        artwork={artwork}
        isPlaying={isPlaying}
        isExpanded={isExpanded}
        currentTime={currentTime}
        duration={duration}
        onToggleExpand={() => setIsExpanded((v) => !v)}
        onTogglePlayPause={togglePlayPause}
        onSeek={seekTo}
        onJump={jump}
        onStop={stopPlayback}
        formatTime={formatTime}
      />
    </Box>,
    document.body
  );
}

function MiniPlayerChrome({
  title,
  artist,
  artwork,
  isPlaying,
  isExpanded,
  currentTime,
  duration,
  onToggleExpand,
  onTogglePlayPause,
  onSeek,
  onJump,
  onStop,
  formatTime,
}: {
  title: string;
  artist: string;
  artwork: string;
  isPlaying: boolean;
  isExpanded: boolean;
  currentTime: number;
  duration: number;
  onToggleExpand: () => void;
  onTogglePlayPause: () => void;
  onSeek: (_: Event, value: number | number[]) => void;
  onJump: (delta: number) => void;
  onStop: () => void;
  formatTime: (seconds: number) => string;
}) {
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            flexShrink: 0,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.25)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
            background: artwork
              ? `url(${artwork}) center / cover no-repeat`
              : "radial-gradient(circle at 30% 20%, rgba(165,180,252,0.9), rgba(67,56,202,0.9))",
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1, cursor: "pointer" }} onClick={onToggleExpand}>
          <Box
            component="div"
            sx={{
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title || "Now playing"}
          </Box>
          {!!artist && (
            <Box
              component="div"
              sx={{
                mt: 0.2,
                fontSize: 11,
                opacity: 0.8,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {artist}
            </Box>
          )}
        </Box>

        <IconButton
          onClick={onTogglePlayPause}
          sx={{
            width: 34,
            height: 34,
            background: "rgba(255,255,255,0.16)",
            color: "#fff",
            "&:hover": { background: "rgba(255,255,255,0.22)" },
          }}
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 18 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>

        <IconButton
          onClick={onToggleExpand}
          sx={{
            width: 30,
            height: 30,
            color: "rgba(255,255,255,0.86)",
            transform: isExpanded ? "rotate(180deg)" : "none",
            transition: "transform 0.2s ease",
          }}
        >
          <ExpandMoreIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          onClick={onStop}
          sx={{
            width: 30,
            height: 30,
            color: "rgba(255,255,255,0.78)",
            background: "rgba(255,255,255,0.06)",
            "&:hover": { background: "rgba(255,255,255,0.14)" },
          }}
          title="Close player"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {isExpanded && (
        <Box sx={{ mt: 1 }}>
          <Slider
            size="small"
            min={0}
            max={Math.max(duration, 1)}
            value={Math.min(currentTime, Math.max(duration, 1))}
            onChange={onSeek}
            sx={{
              color: "#9ec2ff",
              py: 0.25,
              "& .MuiSlider-thumb": { width: 10, height: 10 },
            }}
          />
          <Box
            sx={{
              mt: 0.1,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              color: "rgba(255,255,255,0.75)",
            }}
          >
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </Box>
          <Box
            sx={{
              mt: 0.6,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <IconButton onClick={() => onJump(-10)} sx={{ color: "#fff" }}>
              <SkipPreviousIcon />
            </IconButton>
            <IconButton
              onClick={onTogglePlayPause}
              sx={{
                width: 42,
                height: 42,
                background: "rgba(255,255,255,0.18)",
                color: "#fff",
                "&:hover": { background: "rgba(255,255,255,0.26)" },
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton onClick={() => onJump(10)} sx={{ color: "#fff" }}>
              <SkipNextIcon />
            </IconButton>
            <IconButton onClick={onStop} sx={{ color: "rgba(255,255,255,0.78)" }} title="Stop">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}
    </>
  );
}

export default CardAudioMiniPlayer;
